import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { FaExclamationCircle } from "react-icons/fa";

import { UseOutletContextCompany } from "../Company";
import { StateLanguage } from "../../../../languages/config/StateLanguage";
import { ApiDigitalAuthCompany } from "../../../../services/auth/company.service";
import { Error } from "../../../../models/error.model";
import { Message } from "../../../../models/message.model";
import { GlobalSwal } from "../../../../styles/swal.style";
import {
  legendIconInvalid,
  legendIconValidRequired,
  legendValidInvalidRestart,
  legendIconValidInvalidRestart,
  legendInvalid,
} from "../../../../utils/legend.data.util";
import { uploadTooltip } from "../../../../utils/tooltip.util";
import { evaluateLegendOptionalIcon, evaluateLegendRequiredIcon } from "../../../../scripts/validate.legend.script";
import { expressions } from "../../../../libraries/regular.expressions.library";
import { getError, getMessage } from "../../../../languages/translations/response";
import { uploadUrl } from "../../../../scripts/file.load.script";
import { errorWebHandler } from "../../../../scripts/error.web.handler.script";

import AppInputCountry from "../../../../components/element/InputCountry";
import AppInputLanguage from "../../../../components/element/InputLanguage";
import { countryName } from "../../../../libraries/country.library";
import { ALLOWED_COMPANY_COUNTRIES } from "../../../../config/company-constraints";
import AppInputCanton from "../../../../components/element/InputCanton";
import { resolveCanton, getCantonName } from "../../../../data/cantons";
import AppInputCurrency from "../../../../components/element/InputCurrency";
import { legalFormList } from "../../../../libraries/legal.form.library";
import AppLoading from "../../../../components/Loading";
import AppLegend from "../../../../components/element/Legend";
import AppInputImage from "../../../../components/element/InputImage";

export interface AppCompanyUpdateInformationProps {}

let errorResponse: Error, messageResponse: Message;

const AppCompanyUpdateInformation: React.FunctionComponent<AppCompanyUpdateInformationProps> = (props) => {
  const { appServiceForUser, companyForUser, reloadSession } = UseOutletContextCompany();
  const { lang } = StateLanguage();

  const [loadIndicator, setLoadIndicator] = useState("off");
  const [name, setName] = useState({ value: "", valid: false });
  const [logo, setLogo] = useState({
    value: { name: "", size: 0, type: "", view: "", file: null as any },
    valid: { file: true, message: "" },
  });
  const [street, setStreet] = useState({ value: "", valid: false });
  const [streetNumber, setStreetNumber] = useState({ value: "", valid: false });
  const [additional, setAdditional] = useState({ value: "", valid: false });
  const [administrativeArea, setAdministrativeArea] = useState({ value: "", valid: false });
  // Original value from API, kept to display a warning if it can't be resolved to a known canton
  const [originalAdminArea, setOriginalAdminArea] = useState("");
  const [city, setCity] = useState({ value: "", valid: false });
  const [zipCode, setZipCode] = useState({ value: "", valid: false });
  const [country, setCountry] = useState({ value: "", valid: false });
  const [email, setEmail] = useState({ value: "", valid: false });
  const [phone, setPhone] = useState({ value: "", valid: false });
  const [website, setWebsite] = useState({ value: "", valid: false });
  const [vatNumber, setVatNumber] = useState({ value: "", valid: true });
  const [currency, setCurrency] = useState({ value: "", valid: false, object: null as any });
  const [language, setLanguage] = useState({ value: "", valid: false });
  const [legalForm, setLegalForm] = useState({ value: "", valid: true });

  const submitCompanyUpdate = async () => {
    setLoadIndicator("on");

    if (
      appServiceForUser &&
      companyForUser &&
      name.valid &&
      logo.valid.file &&
      street.valid &&
      streetNumber.valid &&
      additional.valid &&
      administrativeArea.valid &&
      city.valid &&
      zipCode.valid &&
      country.valid &&
      email.valid &&
      phone.valid &&
      website.valid &&
      currency.valid &&
      language.valid
    ) {
      let addressStreetNumber =
        streetNumber.value === "NN" || streetNumber.value === "nn" ? streetNumber.value.toUpperCase() : streetNumber.value;
      // Send FR canonical name to preserve current backend contract (label, not code).
      // If the user hasn't selected a canton (unresolved existing value), keep the original.
      let cantonLabel = administrativeArea.value
        ? getCantonName(administrativeArea.value, 'fr')
        : originalAdminArea;

      await ApiDigitalAuthCompany.putCompany(
        appServiceForUser.api_key,
        companyForUser.id,
        name.value,
        logo.value.file,
        street.value,
        addressStreetNumber,
        additional.value,
        cantonLabel,
        city.value,
        zipCode.value,
        country.value,
        email.value,
        phone.value,
        website.value,
        companyForUser.register_number,
        vatNumber.value,
        currency.value,
        currency.object?.name,
        currency.object?.symbol,
        language.value,
        legalForm.value,
      )
        .then((response) => {
          if (response.status === 200) {
            messageResponse = response.data;

            
            GlobalSwal.fire({
              title: getMessage(messageResponse.message, lang.code),
              text: lang.labels.actionCompletedReturningToPage,
              icon: "success",
              showConfirmButton: false,
              timer: 2200,
            }).then(() => {
              restartForm();
            });
            
          } else {
            errorResponse = response.data;

            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: "error",
              confirmButtonText: lang.labels.okGotIt,
            });
          }
        })
        .catch((error) => {
          errorWebHandler(lang, error);
        })
        .finally(() => {
          setLoadIndicator("off");
        });
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: "error",
        showConfirmButton: false,
        timer: 2200,
      }).then(() => {
        if (!name.value) legendIconInvalid("input-name", "legend-name-required");
        if (!street.value) legendIconInvalid("input-street", "legend-street-required");
        if (!streetNumber.value) legendIconInvalid("input-street-number", "legend-street-number-required");
        if (!administrativeArea.value) legendInvalid("legend-administrative-area-required");
        if (!city.value) legendIconInvalid("input-city", "legend-city-required");
        if (!zipCode.value) legendIconInvalid("input-zip-code", "legend-zip-code-required");
        if (!country.value) legendInvalid("legend-country-required");
        if (!email.value) legendIconInvalid("input-email", "legend-email-required");
        if (!phone.value) legendIconInvalid("input-phone", "legend-phone-required");
        if (!currency.value) legendInvalid("legend-currency-required");
        if (!language.value) legendInvalid("legend-language-required");
        setLoadIndicator("off");
      });
    }
  };

  const handleChangeName = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
    setName({ ...name, value: event.target.value });
  };

  const handleChangeStreet = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
    setStreet({ ...street, value: event.target.value });
  };

  const handleChangeStreetNumber = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
    setStreetNumber({ ...streetNumber, value: event.target.value });
  };

  const handleChangeAdditional = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
    setAdditional({ ...additional, value: event.target.value });
  };

  const handleChangeCity = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
    setCity({ ...city, value: event.target.value });
  };

  const handleChangeZipCode = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
    setZipCode({ ...zipCode, value: event.target.value });
  };

  const handleChangeEmail = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
    setEmail({ ...email, value: event.target.value });
  };

  const handleChangePhone = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
    setPhone({ ...phone, value: event.target.value });
  };

  const handleChangeWebsite = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
    setWebsite({ ...website, value: event.target.value });
  };

  const handleChangeVatNumber = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
    setVatNumber({ ...vatNumber, value: event.target.value });
  };

  const validateName = () => {
    evaluateLegendRequiredIcon(expressions.name, name, setName, "input-name", "legend-name-valid", "legend-name-required");
  };

  const validateStreet = () => {
    evaluateLegendRequiredIcon(expressions.street, street, setStreet, "input-street", "legend-street-valid", "legend-street-required");
  };

  const validateStreetNumber = () => {
    if (streetNumber.value === "NN" || streetNumber.value === "nn") {
      setStreetNumber({ ...streetNumber, valid: true });
      legendIconValidRequired("input-street-number", "legend-street-number-valid", "legend-street-number-required");
    } else {
      evaluateLegendRequiredIcon(
        expressions.streetnumber,
        streetNumber,
        setStreetNumber,
        "input-street-number",
        "legend-street-number-valid",
        "legend-street-number-required",
      );
    }
  };

  const validateAdditional = () => {
    evaluateLegendOptionalIcon(expressions.text, additional, setAdditional, "input-additional", "legend-additional-valid");
  };

  const validateCity = () => {
    evaluateLegendRequiredIcon(expressions.location, city, setCity, "input-city", "legend-city-valid", "legend-city-required");
  };

  const validateZipCode = () => {
    evaluateLegendRequiredIcon(
      expressions.zipcode,
      zipCode,
      setZipCode,
      "input-zip-code",
      "legend-zip-code-valid",
      "legend-zip-code-required",
    );
  };

  const validateEmail = () => {
    evaluateLegendRequiredIcon(expressions.email, email, setEmail, "input-email", "legend-email-valid", "legend-email-required");
  };

  const validatePhone = () => {
    evaluateLegendRequiredIcon(expressions.phone, phone, setPhone, "input-phone", "legend-phone-valid", "legend-phone-required");
  };

  const validateWebsite = () => {
    evaluateLegendOptionalIcon(expressions.website, website, setWebsite, "input-website", "legend-website-valid");
  };

  const validateVatNumber = () => {
    evaluateLegendOptionalIcon(expressions.vat, vatNumber, setVatNumber, "input-vat-number", "legend-vat-number-valid");
  };

  const executeFormRestart = () => {
    uploadData();
    restartForm();
  };

  function restartForm() {
    legendIconValidInvalidRestart("input-name", "legend-name-valid");
    legendIconValidInvalidRestart("input-name", "legend-name-required");
    legendValidInvalidRestart("legend-logo-type");
    legendValidInvalidRestart("legend-logo-size");
    legendIconValidInvalidRestart("input-street", "legend-street-valid");
    legendIconValidInvalidRestart("input-street", "legend-street-required");
    legendIconValidInvalidRestart("input-street-number", "legend-street-number-valid");
    legendIconValidInvalidRestart("input-street-number", "legend-street-number-required");
    legendIconValidInvalidRestart("input-additional", "legend-additional-valid");
    legendValidInvalidRestart("legend-administrative-area-required");
    legendIconValidInvalidRestart("input-city", "legend-city-valid");
    legendIconValidInvalidRestart("input-city", "legend-city-required");
    legendIconValidInvalidRestart("input-zip-code", "legend-zip-code-valid");
    legendIconValidInvalidRestart("input-zip-code", "legend-zip-code-required");
    legendValidInvalidRestart("legend-country-required");
    legendIconValidInvalidRestart("input-email", "legend-email-valid");
    legendIconValidInvalidRestart("input-email", "legend-email-required");
    legendIconValidInvalidRestart("input-phone", "legend-phone-valid");
    legendIconValidInvalidRestart("input-phone", "legend-phone-required");
    legendIconValidInvalidRestart("input-website", "legend-website-valid");
    legendIconValidInvalidRestart("input-vat-number", "legend-vat-number-valid");
    legendValidInvalidRestart("legend-currency-required");
    legendValidInvalidRestart("legend-language-required");
  }

  function uploadData() {
    if (companyForUser) {
      setName({ value: companyForUser.name, valid: true });
      setLogo(uploadUrl(lang, companyForUser.logo ?? ''));
      setStreet({ value: companyForUser.address?.street, valid: true });
      setStreetNumber({ value: companyForUser.address?.number, valid: true });
      setAdditional({ value: companyForUser.address?.additional, valid: true });
      const rawAdminArea = companyForUser.address?.administrative_area || '';
      setOriginalAdminArea(rawAdminArea);
      const resolved = resolveCanton(rawAdminArea);
      setAdministrativeArea(resolved
        ? { value: resolved.code, valid: true }
        : { value: '', valid: rawAdminArea.length === 0 }  // empty is "not yet set", non-empty unresolved needs user action
      );
      setCity({ value: companyForUser.address?.city, valid: true });
      setZipCode({ value: companyForUser.address?.zip_code, valid: true });
      setCountry({ value: companyForUser.address?.country, valid: true });
      setEmail({ value: companyForUser.email, valid: true });
      setPhone({ value: companyForUser.phone, valid: true });
      setWebsite({ value: companyForUser.website, valid: true });
      setVatNumber({ value: companyForUser.vat_number, valid: true });
      setCurrency({
        value: companyForUser.currency?.code ?? '',
        object: companyForUser.currency ?? null,
        valid: !!companyForUser.currency?.code
      })
      setLanguage({ value: companyForUser.language, valid: true });
      setLegalForm({ value: companyForUser.legal_form ?? '', valid: true });
    }
  }

  useEffect(() => {
    uploadData();
    uploadTooltip();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser]);

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title fs-4 fw-bolder">{lang.labels.companyInformationSettingGeneral}</div>
      </div>
      {companyForUser ? (
        <div className="form">
          <div className="card-body">
            <div className="row mb-3 mb-xl-5">
              <div className="col-xl-3">
                <div className="col-form-label required">{lang.labels.name}</div>
              </div>
              <div className="col-xl-9 fv-row fv-plugins-icon-container">
                <input
                  id="input-name"
                  className="form-control"
                  type="text"
                  autoComplete="off"
                  name="name"
                  value={name.value}
                  onChange={handleChangeName}
                  onKeyUp={validateName}
                  onBlur={validateName}
                />
                <AppLegend
                  attribute={{ check: name.valid, name: "name", index: null, sub_index: null }}
                  container={{ valid: true, required: true, identical: false, exists: false }}
                ></AppLegend>
              </div>
            </div>
            <div className="row mb-3 mb-xl-5">
              <div className="col-xl-3">
                <div className="col-form-label">{lang.labels.legalForm}</div>
              </div>
              <div className="col-xl-9 fv-row">
                <Form.Select className="form-select" name="legal_form" value={legalForm.value} onChange={(e) => setLegalForm({value: e.target.value, valid: true})}>
                  <option value="">{lang.labels.select}</option>
                  { legalFormList(lang).map((item, index) => (
                    <option key={index} value={item.code}>{item.display}</option>
                  ))}
                </Form.Select>
              </div>
            </div>
            <div className="row mb-3 mb-xl-5">
              <div className="col-xl-3">
                <div className="col-form-label">{lang.labels.logo}</div>
              </div>
              <div className="col-lg-8">
                <AppInputImage image={logo} setImage={setLogo}></AppInputImage>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-xl-3">
                <div className="col-form-label required">{lang.labels.street}</div>
              </div>
              <div className="col-xl-9 fv-row fv-plugins-icon-container">
                <input
                  id="input-street"
                  className="form-control"
                  type="text"
                  autoComplete="off"
                  name="street"
                  value={street.value}
                  onChange={handleChangeStreet}
                  onKeyUp={validateStreet}
                  onBlur={validateStreet}
                />
                <AppLegend
                  attribute={{ check: street.valid, name: "street", index: null, sub_index: null }}
                  container={{ valid: true, required: true, identical: false, exists: false }}
                ></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-xl-3">
                <div className="col-form-label">
                  <div className="d-flex align-items-center">
                    <span className="required">{lang.labels.number}</span>
                    <i
                      className="ms-2 fs-8"
                      data-bs-toggle="tooltip"
                      data-bs-trigger="hover"
                      data-bs-original-title={lang.labels.ifAddressDoesNotHaveANumber}
                    >
                      <FaExclamationCircle />
                    </i>
                  </div>
                </div>
              </div>
              <div className="col-xl-9 fv-row fv-plugins-icon-container">
                <input
                  id="input-street-number"
                  className="form-control"
                  type="text"
                  autoComplete="off"
                  name="street-number"
                  value={streetNumber.value}
                  onChange={handleChangeStreetNumber}
                  onKeyUp={validateStreetNumber}
                  onBlur={validateStreetNumber}
                />
                <AppLegend
                  attribute={{ check: streetNumber.valid, name: "street-number", index: null, sub_index: null }}
                  container={{ valid: true, required: true, identical: false, exists: false }}
                ></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-xl-3">
                <div className="col-form-label">
                  <div className="d-flex align-items-center">
                    {lang.labels.additional}
                    <i
                      className="ms-2 fs-8"
                      data-bs-toggle="tooltip"
                      data-bs-trigger="hover"
                      data-bs-original-title={lang.labels.ifNecessaryEnterSomethingReferentialRegardingAddress}
                    >
                      <FaExclamationCircle />
                    </i>
                  </div>
                </div>
              </div>
              <div className="col-xl-9 fv-row fv-plugins-icon-container">
                <input
                  id="input-additional"
                  className="form-control"
                  type="text"
                  autoComplete="off"
                  name="additional"
                  value={additional.value}
                  onChange={handleChangeAdditional}
                  onKeyUp={validateAdditional}
                  onBlur={validateAdditional}
                />
                <AppLegend
                  attribute={{ check: additional.valid, name: "additional", index: null, sub_index: null }}
                  container={{ valid: true, required: false, identical: false, exists: false }}
                ></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-xl-3">
                <div className="col-form-label required">{lang.labels.administrativeArea}</div>
              </div>
              <div className="col-xl-9 fv-row fv-plugins-icon-container">
                <AppInputCanton options={{ disabled: false }} canton={administrativeArea} setCanton={setAdministrativeArea} />
                {originalAdminArea && !administrativeArea.value && (
                  <div className="text-warning fs-8 mt-1">
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    {lang.labels.cantonUnresolved || `Current value "${originalAdminArea}" could not be matched. Please select a canton.`}
                  </div>
                )}
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-xl-3">
                <div className="col-form-label">
                  <div className="d-flex align-items-center">
                    <span className="required">{lang.labels.zipCode}</span>
                    <i
                      className="ms-2 fs-8"
                      data-bs-toggle="tooltip"
                      data-bs-trigger="hover"
                      data-bs-original-title={lang.labels.enterThePostalCodeOfTheCity}
                    >
                      <FaExclamationCircle />
                    </i>
                  </div>
                </div>
              </div>
              <div className="col-xl-9 fv-row fv-plugins-icon-container">
                <input
                  id="input-zip-code"
                  className="form-control"
                  type="text"
                  autoComplete="off"
                  name="zip-code"
                  value={zipCode.value}
                  onChange={handleChangeZipCode}
                  onKeyUp={validateZipCode}
                  onBlur={validateZipCode}
                />
                <AppLegend
                  attribute={{ check: zipCode.valid, name: "zip-code", index: null, sub_index: null }}
                  container={{ valid: true, required: true, identical: false, exists: false }}
                ></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-xl-3">
                <div className="col-form-label required">{lang.labels.city}</div>
              </div>
              <div className="col-xl-9 fv-row fv-plugins-icon-container">
                <input
                  id="input-city"
                  className="form-control"
                  type="text"
                  autoComplete="off"
                  name="city"
                  value={city.value}
                  onChange={handleChangeCity}
                  onKeyUp={validateCity}
                  onBlur={validateCity}
                />
                <AppLegend
                  attribute={{ check: city.valid, name: "city", index: null, sub_index: null }}
                  container={{ valid: true, required: true, identical: false, exists: false }}
                ></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-xl-3">
                <div className="col-form-label required">{lang.labels.country}</div>
              </div>
              <div className="col-xl-9 fv-row fv-plugins-icon-container">
                {ALLOWED_COMPANY_COUNTRIES.length === 1
                  ? <input className="form-control" type="text" value={countryName(lang.code, country.value) || country.value} disabled />
                  : <AppInputCountry
                      options={{ small: false, disabled: false, placeholder: null }}
                      country={country}
                      setCountry={setCountry}
                    ></AppInputCountry>
                }
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-xl-3">
                <div className="col-form-label required">{lang.labels.email}</div>
              </div>
              <div className="col-xl-9 fv-row fv-plugins-icon-container">
                <input
                  id="input-email"
                  className="form-control"
                  type="email"
                  autoComplete="off"
                  name="email"
                  value={email.value}
                  onChange={handleChangeEmail}
                  onKeyUp={validateEmail}
                  onBlur={validateEmail}
                />
                <AppLegend
                  attribute={{ check: email.valid, name: "email", index: null, sub_index: null }}
                  container={{ valid: true, required: true, identical: false, exists: false }}
                ></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-xl-3">
                <div className="col-form-label required">{lang.labels.phoneNumber}</div>
              </div>
              <div className="col-xl-9 fv-row fv-plugins-icon-container">
                <input
                  id="input-phone"
                  className="form-control"
                  type="tel"
                  autoComplete="off"
                  name="phone"
                  value={phone.value}
                  onChange={handleChangePhone}
                  onKeyUp={validatePhone}
                  onBlur={validatePhone}
                />
                <AppLegend
                  attribute={{ check: phone.valid, name: "phone", index: null, sub_index: null }}
                  container={{ valid: true, required: true, identical: false, exists: false }}
                ></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-xl-3">
                <div className="col-form-label">{lang.labels.webSite}</div>
              </div>
              <div className="col-xl-9 fv-row fv-plugins-icon-container">
                <input
                  id="input-website"
                  className="form-control"
                  type="url"
                  autoComplete="off"
                  name="website"
                  value={website.value}
                  onChange={handleChangeWebsite}
                  onKeyUp={validateWebsite}
                  onBlur={validateWebsite}
                />
                <AppLegend
                  attribute={{ check: website.valid, name: "website", index: null, sub_index: null }}
                  container={{ valid: true, required: false, identical: false, exists: false }}
                ></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-xl-3">
                <div className="col-form-label">
                  <div className="d-flex align-items-center">
                    {lang.labels.vatNumber}
                    <i
                      className="ms-2 fs-8"
                      data-bs-toggle="tooltip"
                      data-bs-trigger="hover"
                      data-bs-original-title={lang.labels.identificationNumberForACompany}
                    >
                      <FaExclamationCircle />
                    </i>
                  </div>
                </div>
              </div>
              <div className="col-xl-9 fv-row fv-plugins-icon-container">
                <input
                  id="input-vat-number"
                  className="form-control"
                  type="text"
                  autoComplete="off"
                  name="vat-number"
                  value={vatNumber.value}
                  onChange={handleChangeVatNumber}
                  onKeyUp={validateVatNumber}
                  onBlur={validateVatNumber}
                />
                <AppLegend
                  attribute={{ check: vatNumber.valid, name: "vat-number", index: null, sub_index: null }}
                  container={{ valid: true, required: false, identical: false, exists: false }}
                ></AppLegend>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-xl-3">
                <div className="col-form-label">
                  <div className="d-flex align-items-center">
                    <span className="required">{lang.labels.currency}</span>
                    <i
                      className="ms-2 fs-8"
                      data-bs-toggle="tooltip"
                      data-bs-trigger="hover"
                      data-bs-original-title={lang.labels.basicCurrencyCompany}
                    >
                      <FaExclamationCircle />
                    </i>
                  </div>
                </div>
              </div>
              <div className="col-xl-9 fv-row fv-plugins-icon-container">
                <AppInputCurrency currency={currency} setCurrency={setCurrency}></AppInputCurrency>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-xl-3">
                <div className="col-form-label">
                  <div className="d-flex align-items-center">
                    <span className="required">{lang.labels.language}</span>
                    <i
                      className="ms-2 fs-8"
                      data-bs-toggle="tooltip"
                      data-bs-trigger="hover"
                      data-bs-original-title={lang.labels.languageToDownload}
                    >
                      <FaExclamationCircle />
                    </i>
                  </div>
                </div>
              </div>
              <div className="col-xl-9 fv-row fv-plugins-icon-container">
                <AppInputLanguage language={language} setLanguage={setLanguage}></AppInputLanguage>
              </div>
            </div>
          </div>
          <div className="card-footer d-flex justify-content-end">
            <button className="btn btn-secondary btn-active-light-primary" type="reset" onClick={executeFormRestart}>
              {lang.labels.discard}
            </button>
            <button className="btn btn-primary ms-2" type="button" data-kt-indicator={loadIndicator} onClick={submitCompanyUpdate}>
              <span className="indicator-label">{lang.labels.saveChanges}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      ) : (
        <AppLoading></AppLoading>
      )}
    </div>
  );
};

export default AppCompanyUpdateInformation;